import { useState, useRef, useMemo } from 'react';
import PageHeader from '../components/PageHeader';
import { useCachedResource } from '../hooks/useCachedResource';
import { usePageRefresh } from '../context/PullToRefreshContext';
import { findOverlappingEntryIds, type OverlapEntry } from '../utils/timesheetOverlap';
import { Link } from 'react-router-dom';
import DayShapeNotice from '../components/DayShapeNotice';
import GapFillChooser from '../components/GapFillChooser';
import UnassignedTimeStrip from '../components/UnassignedTimeStrip';
import {
  findGaps,
  findOverlaps,
  gapDriveCandidate,
  isImplausibleDriveSpan,
  odometerMiles,
  unassignedMinutes,
} from '../utils/dayLegs';
import type { DayProblem } from '../utils/dayLegs';
import { buildItinerary, itineraryTotals, type ItineraryEntry } from '../utils/timesheetItinerary';
import TimesheetItinerary from '../components/TimesheetItinerary';
import ManualCallEntry from '../components/ManualCallEntry';
import { callLogsApi, CallLog, isVisitSigned, isVisitSkipped } from '../services/api';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import ClockPunches from '../components/ClockPunches';
import {
  getEditablePeriodDescription,
  isInGracePeriod,
  isDateEditable,
  formatDateToLocal,
} from '../utils/timesheetRules';

interface ApiTimeEntry {
  id: number;
  store: string;
  type: 'store' | 'drive';
  start: string;
  end: string;
  duration: string;
  /** Numeric minutes for this entry (0 for an active/in-progress row). Used by
   *  the itinerary grouping to sum project + visit time. */
  durationMin: number;
  /** Miles for a drive leg (odometer delta), shown on drive rows. */
  miles?: number;
  /** A drive kept on the timeline but left out of every total — see the drive
   *  rule in `dayLegs`. Decided once here, where the odometer readings are. */
  excludedFromTotals?: boolean;
  callLogId?: number;
  projectId?: number | null;
  projectDescription?: string;
}


interface DaySummary {
  totalHours: number;
  driveHours: number;
  storeHours: number;
  mileage: number;
  entries: ApiTimeEntry[];
  callLogs: CallLog[]; // Raw CallLog data for editing
}

interface WeekDayData {
  day: string;
  date: string; // YYYY-MM-DD format for grace period check
  hours: number;
  stores: number;
  entries: ApiTimeEntry[];
  callLogs: CallLog[]; // Raw CallLog data for editing
}

interface WeekSummary {
  totalHours: number;
  driveHours: number;
  storeHours: number;
  totalMileage: number;
  days: WeekDayData[];
  allCallLogs: CallLog[]; // All call logs for the week
}

// Helper to parse time string to minutes since midnight
// Handles ISO datetime, "HH:MM:SS" (24hr), and "h:mm AM/PM" (12hr) formats
function parseTimeToMinutes(time: string | null): number | null {
  if (!time) return null;

  // Handle ISO datetime format (e.g., "2026-01-31T09:45:00-05:00")
  if (time.includes('T')) {
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        return date.getHours() * 60 + date.getMinutes();
      }
    } catch {
      // Fall through to other parsing methods
    }
  }

  // Check if it's 12-hour format (contains AM/PM)
  if (time.includes('AM') || time.includes('PM')) {
    const isPM = time.includes('PM');
    const timePart = time.replace(/\s*(AM|PM)/i, '');
    const [hourStr, minStr] = timePart.split(':');
    let hours = parseInt(hourStr);
    const minutes = parseInt(minStr);

    // Convert to 24-hour format
    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  // Otherwise assume 24-hour format (HH:MM or HH:MM:SS)
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

// Helper to format time - handles ISO datetime, HH:MM:SS, or AM/PM formats
function formatTime(time: string | null): string {
  if (!time) return '';

  // If already in AM/PM format, return as-is
  if (time.includes('AM') || time.includes('PM')) {
    return time;
  }

  // Handle ISO datetime format (e.g., "2026-01-31T09:45:00-05:00")
  if (time.includes('T')) {
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        const h = date.getHours();
        const m = date.getMinutes();
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
      }
    } catch {
      // Fall through to simple parsing
    }
  }

  // Convert from HH:MM:SS to h:mm AM/PM
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  if (isNaN(h)) return time; // Return as-is if can't parse
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Helper to format Date to h:mm AM/PM
function formatDateTime(date: Date): string {
  const h = date.getHours();
  const m = date.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// Helper to calculate duration between two times (in minutes).
// Exported for unit testing (#3450 midnight-safety).
/** Mirrors apps/projects/duration.DAY_MINUTES — the two must agree. */
const DAY_MINUTES = 24 * 60;

export function calculateDuration(start: string | null, end: string | null): number {
  if (!start || !end) return 0;
  // When both carry a full date (ISO), use real elapsed time so a span crossing
  // midnight — or running >24h — is correct. parseTimeToMinutes collapses to
  // minutes-since-midnight (it drops the date), which made a cross-midnight span
  // negative and the timesheet show garbage totals (#3450).
  if (start.includes('T') && end.includes('T')) {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (!Number.isNaN(s) && !Number.isNaN(e)) {
      const total = Math.max(0, Math.round((e - s) / 60000));
      // A stored span of a day or more is an overnight date-adjustment or a
      // mis-typed end date, never a day of work — keep only the within-day
      // part, exactly as the server's duration.span_minutes does. Without this
      // the Time page would show 27h against the server's 3h for the same row.
      return total >= DAY_MINUTES ? total % DAY_MINUTES : total;
    }
  }
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return 0;
  // Time-only strings carry no date; a negative is a bad row, not a real
  // overnight (real overnights arrive as dated ISO above) — clamp to 0 to match
  // the server, never wrap to ~23h44m.
  return Math.max(0, endMinutes - startMinutes);
}

// Helper to process call logs into time entries
// The API returns EXPANDED records where each calllog is split into Drive Time, Work, and Drive Back
// Exported for unit testing (chronological-ordering guard, diag #45).
export function processCallLogs(callLogs: CallLog[]): {
  entries: ApiTimeEntry[];
  totalStoreMinutes: number;
  totalDriveMinutes: number;
  totalMileage: number;
} {
  // Collect each event with a chronological sort key (its real start instant)
  // so the timeline reads in true event order, not the API's calllog-desc order
  // (diag #45). stcalltime is the start instant for BOTH drive and store rows
  // (the API expands each calllog into separate Drive / Work / Drive Back rows,
  // each carrying its own stcalltime/edcalltime).
  const collected: Array<{ entry: ApiTimeEntry; sortMs: number }> = [];
  let totalStoreMinutes = 0;
  let totalDriveMinutes = 0;
  let totalMileage = 0;

  for (const log of callLogs) {
    const activity = log.description_of_activity || '';
    const isDriveTime = activity.includes('Drive Time');
    const isDriveBack = activity.includes('Drive Back');
    const isPending = activity === 'Scheduled Visit';

    // Calculate duration using stcalltime/edcalltime (the actual times for this entry type)
    const durationMinutes = (log.stcalltime && log.edcalltime)
      ? calculateDuration(log.stcalltime, log.edcalltime)
      : 0;
    const startMs = log.stcalltime ? new Date(log.stcalltime).getTime() : NaN;
    const sortMs = Number.isNaN(startMs) ? Number.MAX_SAFE_INTEGER : startMs;

    if (isDriveTime || isDriveBack) {
      // Drive entry - use stcalltime/edcalltime as start/end (API already set these correctly)
      // A drive too long to be one is a forgotten punch. The leg chain leaves it
      // out of Today's total (dayLegs.sumClosed) and the same rule has to reach
      // here, or one rep reads two different drive times on two screens. The
      // odometer pair is the evidence, so it comes from this row's own readings —
      // `totalmiles` cannot tell a real zero-mile pair from no reading at all.
      const driveMiles = isDriveBack
        ? odometerMiles(log.drivebackodostart, log.drivebackodoend)
        : odometerMiles(log.driveodostart, log.driveodoend);
      const excludedFromTotals = isImplausibleDriveSpan(durationMinutes, driveMiles);
      if (!excludedFromTotals) {
        totalDriveMinutes += durationMinutes;
      }
      collected.push({
        sortMs,
        entry: {
          id: log.calllogid * 10 + (isDriveBack ? 1 : 0),
          store: isDriveBack ? 'Drive Back' : 'Drive',
          type: 'drive',
          start: formatTime(log.stcalltime),
          end: formatTime(log.edcalltime),
          duration: durationMinutes > 0 ? `${durationMinutes} min` : '',
          durationMin: durationMinutes,
          // From this row's own odometer pair: only the pair can tell an
          // affirmed zero-mile drive (shown as "0 mi") from the 0/0
          // no-tracking sentinel, which `totalmiles` collapses to the same
          // 0 (#3618).
          miles: driveMiles ?? undefined,
          excludedFromTotals,
          callLogId: log.calllogid,
          projectId: log.project_id,
          projectDescription: log.project_description,
        },
      });
    } else if (!isPending) {
      // Store/work entry
      totalStoreMinutes += durationMinutes;
      collected.push({
        sortMs,
        entry: {
          id: log.calllogid,
          store: log.retailer_abbreviation
            ? `${log.retailer_abbreviation} #${log.store_legacystoreid}`
            : `Store ${log.store_legacystoreid}`,
          type: 'store',
          start: formatTime(log.stcalltime),
          end: log.edcalltime ? formatTime(log.edcalltime) : 'In Progress',
          duration: log.edcalltime ? `${durationMinutes} min` : 'Active',
          durationMin: log.edcalltime ? durationMinutes : 0,
          callLogId: log.calllogid,
          projectId: log.project_id,
          projectDescription: log.project_description,
        },
      });
    }
    // Skip pending entries - they have no times

    // Mileage
    totalMileage += log.totalmiles || 0;
  }

  // Chronological (ascending start time) — fixes diag #45 out-of-order timeline.
  collected.sort((a, b) => a.sortMs - b.sortMs);
  const entries = collected.map((c) => c.entry);

  return { entries, totalStoreMinutes, totalDriveMinutes, totalMileage };
}

export default function TimeTracking() {
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'prior'>('today');
  // isLoading/error/todayData/weekData/priorWeekData are derived from a
  // cache-first hook + useMemo, declared below (after filterPendingDeleted).

  // Local day tracking
  const {
    dayData,
    clockWindows,
    correctClockPunch,
    deleteLeg,
  } = useDayTrackingContext();

  // Manual call entry modal state
  const [showManualCallEntry, setShowManualCallEntry] = useState(false);
  const [fillingGap, setFillingGap] = useState<DayProblem | null>(null);
  const [editingCallLog, setEditingCallLog] = useState<CallLog | null>(null);

  // Track which day is expanded in week views (for showing detailed entries)
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  // Delete confirmation state
  const [deletingCallLog, setDeletingCallLog] = useState<CallLog | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Optimistically-deleted calllogids (#1987). The DELETE now flows through the
  // offline sync queue and drains asynchronously, so the server list refetch
  // (handleSaveComplete) can still return a just-deleted row before the queue
  // drains. We hold those ids in a ref-backed set and filter every refetch
  // against it so a deleted entry never flickers back. The set lives only for
  // this page session; a full reload re-reads authoritative server state.
  const pendingDeletedIds = useRef<Set<number>>(new Set());

  // Get editable period info
  const editablePeriod = getEditablePeriodDescription();
  const inGracePeriod = isInGracePeriod();

  // Helper to parse date from various formats (MM/DD/YYYY or YYYY-MM-DD)
  const parseDate = (dateStr: string): Date => {
    // Check if it's MM/DD/YYYY format
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid timezone issues
    }
    // Otherwise assume YYYY-MM-DD or ISO format
    const cleanDate = dateStr.split('T')[0];
    const [year, month, day] = cleanDate.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  // Helper to process week data from call logs
  // API returns EXPANDED records - each entry has description_of_activity indicating type
  const processWeekData = (callLogs: CallLog[]): WeekSummary => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // Group by day name AND track the actual date
    const logsByDay = new Map<string, { date: string; logs: CallLog[] }>();

    for (const log of callLogs || []) {
      const date = parseDate(log.dtecall);
      const day = dayNames[date.getDay()];
      // Format date as YYYY-MM-DD for the date field
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      if (!logsByDay.has(day)) {
        logsByDay.set(day, { date: dateStr, logs: [] });
      }
      logsByDay.get(day)!.logs.push(log);
    }

    let weekTotalMinutes = 0;
    let weekDriveMinutes = 0;
    let weekStoreMinutes = 0;
    let weekMileage = 0;
    const days: WeekDayData[] = [];

    for (const dayName of ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']) {
      const dayData = logsByDay.get(dayName);
      const dayLogs = dayData?.logs || [];
      const dayDate = dayData?.date || '';
      let dayMinutes = 0;
      // Count unique store visits (work entries only, not drive entries)
      const storeVisits = new Set<number>();

      // Process call logs to create entries using shared helper
      const { entries: dayEntries, totalStoreMinutes, totalDriveMinutes, totalMileage } = processCallLogs(dayLogs);

      dayMinutes = totalStoreMinutes + totalDriveMinutes;
      weekDriveMinutes += totalDriveMinutes;
      weekStoreMinutes += totalStoreMinutes;
      weekMileage += totalMileage;

      // Count unique store visits
      for (const log of dayLogs) {
        const activity = log.description_of_activity || '';
        const isDriveTime = activity.includes('Drive Time') || activity.includes('Drive Back');
        const isPending = activity === 'Scheduled Visit';
        if (!isDriveTime && !isPending) {
          storeVisits.add(log.calllogid);
        }
      }

      weekTotalMinutes += dayMinutes;
      days.push({
        day: dayName,
        date: dayDate,
        hours: dayMinutes / 60,
        stores: storeVisits.size,
        entries: dayEntries,
        callLogs: dayLogs,
      });
    }

    return {
      totalHours: weekTotalMinutes / 60,
      driveHours: weekDriveMinutes / 60,
      storeHours: weekStoreMinutes / 60,
      totalMileage: weekMileage,
      days,
      allCallLogs: callLogs || [],
    };
  };

  // Drop any call logs the rep optimistically deleted this session so a
  // pre-drain server refetch can't re-add them (#1987).
  const filterPendingDeleted = (logs: CallLog[]): CallLog[] =>
    pendingDeletedIds.current.size === 0
      ? logs
      : logs.filter((log) => !pendingDeletedIds.current.has(log.calllogid));

  // Cache-first time data (#2057): paint the last-cached current+prior week
  // instantly (incl. offline), then revalidate in the background. We cache the
  // RAW call logs and derive the day/week summaries below via useMemo so an
  // optimistic delete just removes from the raw list and the views recompute.
  const {
    data: timeData,
    isLoading,
    error,
    refetch: handleSaveComplete,
    mutate: mutateTime,
  } = useCachedResource<{ callLogs: CallLog[]; priorCallLogs: CallLog[] }>(
    'time-tracking',
    async () => {
      const today = new Date();
      const todayStr = formatDateToLocal(today);
      const callLogs = filterPendingDeleted(await callLogsApi.getCallLogs(todayStr));

      const priorWeekDate = new Date(today);
      priorWeekDate.setDate(priorWeekDate.getDate() - 7);
      const priorWeekStr = formatDateToLocal(priorWeekDate);
      const priorCallLogs = filterPendingDeleted(await callLogsApi.getCallLogs(priorWeekStr));

      return { callLogs: callLogs || [], priorCallLogs: priorCallLogs || [] };
    },
    []
  );

  // #2089: swipe-down refetches the current + prior week call logs.
  usePageRefresh(handleSaveComplete);

  // Derive today's summary from the raw current-week logs. API returns
  // MM/DD/YYYY so we compare parsed dates; expanded Drive/Work/Drive-Back
  // entries are all kept (no dedup) for correct display.
  const todayData = useMemo<DaySummary | null>(() => {
    if (!timeData) return null;
    const today = new Date();
    const todayLogsRaw = timeData.callLogs.filter((log) => {
      const logDate = parseDate(log.dtecall);
      return (
        logDate.getFullYear() === today.getFullYear() &&
        logDate.getMonth() === today.getMonth() &&
        logDate.getDate() === today.getDate()
      );
    });
    const { entries, totalStoreMinutes, totalDriveMinutes, totalMileage } =
      processCallLogs(todayLogsRaw);
    return {
      totalHours: (totalStoreMinutes + totalDriveMinutes) / 60,
      storeHours: totalStoreMinutes / 60,
      driveHours: totalDriveMinutes / 60,
      mileage: totalMileage,
      entries,
      callLogs: todayLogsRaw,
    };
  }, [timeData]);

  // #2070: flag overlapping same-store visit intervals on the Today timesheet
  // (e.g. an active/never-closed check-in plus a later completed visit at the
  // same store) so the rep can review instead of silently double-counting time.
  // Presentational only — auto-close/merge is deferred to an audited server step.
  const overlappingTodayIds = useMemo<Set<number>>(() => {
    if (!todayData) return new Set<number>();
    const overlapEntries: OverlapEntry[] = todayData.entries
      .filter((e) => e.type === 'store')
      .map((e) => ({
        id: e.id,
        storeKey: e.store,
        startMin: parseTimeToMinutes(e.start),
        endMin: parseTimeToMinutes(e.end),
        open: e.duration === 'Active',
      }));
    return findOverlappingEntryIds(overlapEntries);
  }, [todayData]);

  /**
   * Overlaps and gaps in TODAY's leg chain (#3519). Distinct from
   * overlappingTodayIds above, which flags same-store visits in the SERVER
   * projection for the row highlight; this asks whether the rep's punches fit
   * together at all — the question the CallLog validator will ask when these
   * legs try to sync, and the one RMR's rule is about.
   */
  const chainLegs = useMemo(
    () => [...(clockWindows || []), ...(dayData.timeEntries || [])],
    [clockWindows, dayData.timeEntries]
  );

  const dayShapeProblems = useMemo(() => {
    if (!chainLegs.length) return [];
    return [...findOverlaps(chainLegs), ...findGaps(chainLegs)];
  }, [chainLegs]);

  const todayUnassignedMinutes = useMemo(() => unassignedMinutes(dayShapeProblems), [dayShapeProblems]);

  // Day itinerary (visit → project → clocking) for the Today tab. Groups the
  // SAME entries the flat list uses, so its totals must equal the flat totals;
  // if they ever don't, we render the flat list instead (reps never see a wrong
  // number). Force the flat view without a redeploy via
  // localStorage.rdm_timesheet_view = 'flat'.
  const todayItinerary = useMemo(
    () => (todayData ? buildItinerary(todayData.entries as ItineraryEntry[]) : []),
    [todayData]
  );
  const useTodayItinerary = useMemo(() => {
    if (!todayData) return false;
    try {
      if (localStorage.getItem('rdm_timesheet_view') === 'flat') return false;
    } catch { /* ignore */ }
    const t = itineraryTotals(todayItinerary);
    const flatStore = Math.round(todayData.storeHours * 60);
    const flatDrive = Math.round(todayData.driveHours * 60);
    return t.storeMin === flatStore && t.driveMin === flatDrive;
  }, [todayData, todayItinerary]);

  const weekData = useMemo<WeekSummary | null>(
    () => (timeData ? processWeekData(timeData.callLogs) : null),
    [timeData]
  );
  const priorWeekData = useMemo<WeekSummary | null>(
    () => (timeData ? processWeekData(timeData.priorCallLogs) : null),
    [timeData]
  );

  // Handle editing a completed API call log - open in modal
  const handleEditCallLog = (callLog: CallLog) => {
    setEditingCallLog(callLog);
    setShowManualCallEntry(true);
  };

  // Handle closing the manual entry modal
  const handleCloseManualEntry = () => {
    setShowManualCallEntry(false);
    setEditingCallLog(null);
    setFillingGap(null);
  };

  // The gap's own span, as manual entry's date/time inputs spell it — the rep
  // asserts "this hole was a store visit" and must not retype the hole's
  // boundaries from memory (#3470's resolution).
  const gapSpan = useMemo(() => {
    if (!fillingGap) return undefined;
    const d = fillingGap.from;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      timeIn: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      timeOut: `${pad(fillingGap.to.getHours())}:${pad(fillingGap.to.getMinutes())}`,
    };
  }, [fillingGap]);

  // The drive offer is made only when it can actually land: the candidate's
  // CallLog row must be loaded (the chain's callLogId can outrun the cached
  // week fetch) and must carry no drive of its own — otherwise the seed would
  // be refused and the tap would do nothing, or worse, fall through to
  // creating a duplicate store visit.
  const gapDriveOffer = useMemo(() => {
    if (!fillingGap) return null;
    const candidate = gapDriveCandidate(fillingGap, chainLegs);
    if (!candidate) return null;
    const row = todayData?.callLogs.find((l) => l.calllogid === candidate.callLogId);
    if (!row || row.stdrivetime || row.eddrivetime) return null;
    return { candidate, row };
  }, [fillingGap, chainLegs, todayData]);

  // Immediately strip an optimistically-deleted calllogid out of the rendered
  // day/week state so the entry disappears the instant the rep confirms — no
  // wait on the async queue drain or the refetch.
  const removeCallLogFromDisplay = (callLogId: number) => {
    // Remove from the cached RAW logs; the day/week useMemos recompute. The
    // pendingDeletedIds ref keeps it out of the next revalidation too (#1987).
    mutateTime((prev) =>
      prev
        ? {
            callLogs: prev.callLogs.filter((l) => l.calllogid !== callLogId),
            priorCallLogs: prev.priorCallLogs.filter((l) => l.calllogid !== callLogId),
          }
        : prev
    );
  };

  // Handle delete confirmation. Queue-only (#1987): the offline sync queue is
  // the sole server-write path. We adopt the server-sourced CallLog into a local
  // visit_task_sessions row (so the queue can resolve its calllogid at sync
  // time) and enqueue deleteTimeEntry. The UI updates optimistically and the
  // refetch is filtered so the row can't flicker back before the queue drains.
  const handleDeleteCallLog = async () => {
    if (!deletingCallLog) return;

    const callLogId = deletingCallLog.calllogid;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const { writeOps, adoptServerCallLog } = await import('../sync/writeOps');
      const visitLocalId = await adoptServerCallLog(callLogId, {
        storeId: deletingCallLog.store_legacystoreid,
        projectId: deletingCallLog.project_id ?? undefined,
      });
      await writeOps.deleteTimeEntry.apply({ visitLocalId });

      // Optimistic UI: mark deleted so refetches filter it out, drop it from the
      // rendered list now, close the modal, then refresh.
      pendingDeletedIds.current.add(callLogId);
      removeCallLogFromDisplay(callLogId);
      setDeletingCallLog(null);
      await handleSaveComplete();
    } catch (err) {
      console.error('Failed to delete call log:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete entry';
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // handleSaveComplete is the cache-first hook's refetch (aliased above): it
  // revalidates the raw logs from the server and the useMemos recompute.

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader title="Time Tracking" subtitle="Track your work hours" />

      {/* Tab Switcher */}
      <div className="bg-white px-4 py-2 shadow-sm">
        <div className="flex rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              activeTab === 'today'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setActiveTab('week')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              activeTab === 'week'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            This Week
          </button>
          <button
            onClick={() => setActiveTab('prior')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              activeTab === 'prior'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Prior Week
          </button>
        </div>
      </div>

      <main className="p-4">
        {/* Editable Period Info */}
        <div className="mb-4 rounded-xl bg-slate-100 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-slate-600">
                Editable period: <span className="font-medium">{editablePeriod}</span>
              </span>
            </div>
            {inGracePeriod && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Grace Period
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Cutoff: Sunday 11:59 PM Eastern Time
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="mb-4 rounded-xl bg-amber-50 p-4 text-amber-700">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium">Unable to load API data</p>
                <p className="text-sm">{error}</p>
                <p className="mt-1 text-sm">Showing local tracking data only.</p>
              </div>
            </div>
          </div>
        )}

        {/* Today Tab */}
        {activeTab === 'today' && (
          <>
            {/* Day Status Banner */}
            {dayData.status === 'ended' && (
              <div className="mb-4 rounded-xl bg-green-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Clocked Out</p>
                      <p className="text-sm text-green-600">
                        {dayData.startTime && dayData.endTime && (
                          <>
                            {formatDateTime(dayData.startTime)} - {formatDateTime(dayData.endTime)}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  {/* Clocking in again belongs on Today, where it captures
                      location and the odometer; this page fixes punches. */}
                </div>
              </div>
            )}

            {/* Punch corrections — the fix-anything surface for time the app
                didn't capture the way the day actually went. */}
            <ClockPunches
              windows={clockWindows}
              onCorrect={correctClockPunch}
              onDelete={deleteLeg}
          />

            {/* Today's Summary */}
            {!isLoading && todayData && (
              <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
                <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                  Today's Summary
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-2xl font-bold text-slate-800">
                      {todayData.totalHours.toFixed(1)}h
                    </div>
                    <div className="text-xs text-slate-500">Total Hours</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-2xl font-bold text-slate-800">
                      {todayData.mileage.toFixed(1)}
                    </div>
                    <div className="text-xs text-slate-500">Miles</div>
                  </div>
                  <div className="rounded-lg bg-green-50 p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">
                      {todayData.storeHours.toFixed(1)}h
                    </div>
                    <div className="text-xs text-green-600">Store Time</div>
                  </div>
                  <div className="rounded-lg bg-blue-50 p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">
                      {todayData.driveHours.toFixed(1)}h
                    </div>
                    <div className="text-xs text-blue-600">Drive Time</div>
                  </div>
                </div>
                <UnassignedTimeStrip minutes={todayUnassignedMinutes} />
              </div>
            )}

            {/* The day's own shape, from the leg chain rather than the
                server projection: a gap or an overlap is a fact about how the
                rep's punches fit together, and the chain is where that lives
                (#3519). */}
            {!isLoading && dayShapeProblems.length > 0 && (
              <DayShapeNotice problems={dayShapeProblems} onFillGap={setFillingGap} />
            )}

            {/* Time Entries */}
            {!isLoading && todayData && todayData.entries.length > 0 && (
              <div className="rounded-xl bg-white p-4 shadow-card">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                    Time Log
                  </h2>
                  <button
                    onClick={() => setShowManualCallEntry(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add time
                  </button>
                </div>
                <div className="space-y-2">
                  {useTodayItinerary ? (
                    <TimesheetItinerary
                      items={todayItinerary}
                      callLogs={todayData.callLogs}
                      onEdit={handleEditCallLog}
                      onDelete={setDeletingCallLog}
                    />
                  ) : (
                  todayData.entries.map((entry) => {
                    // Find the corresponding CallLog for this entry (for store entries)
                    // Important: API returns expanded records - find the Work entry, not Drive entry
                    const callLog = entry.type === 'store' && entry.callLogId
                      ? todayData.callLogs.find(log => {
                          if (log.calllogid !== entry.callLogId) return false;
                          // Skip Drive entries - we want the Work entry
                          const activity = log.description_of_activity || '';
                          return !activity.includes('Drive');
                        })
                      : null;
                    // Can edit completed store visits
                    const isCompleted = entry.duration !== 'Active';
                    const canEdit = entry.type === 'store' && isCompleted && callLog;
                    // Check if signed vs deliberately skipped (no sig with reason)
                    const isSigned = callLog ? isVisitSigned(callLog) : false;
                    const isSkipped = callLog ? isVisitSkipped(callLog) : false;

                    return (
                      <div
                        key={entry.id}
                        className={`flex items-center gap-3 rounded-lg border p-3 ${
                          entry.type === 'store'
                            ? 'border-green-200 bg-green-50'
                            : 'border-blue-200 bg-blue-50'
                        }`}
                      >
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${
                            entry.type === 'store' ? 'bg-green-500' : 'bg-blue-500'
                          } text-white`}
                        >
                          {entry.type === 'store' ? (
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{entry.store}</span>
                            {/* Signed Badge — only for real manager signatures */}
                            {isSigned && (
                              <span className="flex items-center gap-0.5 rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Signed
                              </span>
                            )}
                            {/* No Sig Badge — deliberate skip with a reason */}
                            {isSkipped && (
                              <span
                                className="flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700"
                                title={callLog?.no_signature_reason || undefined}
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                                </svg>
                                No Sig
                              </span>
                            )}
                            {/* Edited Badge */}
                            {callLog?.edited_after_signature && (
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                                Edited
                              </span>
                            )}
                            {/* Overlap warning (#2070): two visits at this store
                                share overlapping time — surface it rather than
                                silently double-counting (a visit may be unclosed). */}
                            {overlappingTodayIds.has(entry.id) && (
                              <span
                                className="flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700"
                                title="Overlapping visit times at this store — review; a visit may not have been closed."
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                Overlap
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {entry.start} - {entry.end}
                          </div>
                          {entry.type === 'drive' && entry.miles != null && (
                            <div className="text-xs font-medium text-blue-600">{entry.miles} mi</div>
                          )}
                          {/* Project label (#2062): the Time page labeled rows by SITE
                              only, so two visits at one store (multi-project, or an
                              active + completed pair) looked like duplicates. Show the
                              project per row — parity with the Week / Prior Week views. */}
                          {entry.projectId && entry.type === 'store' && (
                            <div className="text-xs text-primary-600 truncate">
                              Project #{entry.projectId}{entry.projectDescription && `: ${entry.projectDescription}`}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              entry.duration === 'Active'
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-white text-slate-600'
                            }`}
                          >
                            {entry.duration}
                          </div>
                          {/* Edit Button */}
                          {canEdit && callLog && (
                            <button
                              onClick={() => handleEditCallLog(callLog)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              title="Edit visit"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          )}
                          {/* Delete Button */}
                          {canEdit && callLog && !isSigned && (
                            <button
                              onClick={() => setDeletingCallLog(callLog)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-100 hover:text-red-600"
                              title="Delete visit"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                  )}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && (!todayData || todayData.entries.length === 0) && (
              <div className="rounded-xl bg-white p-8 text-center shadow-card">
                <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-medium text-slate-700">No time entries for today</p>
                <p className="mt-1 text-sm text-slate-500">
                  Working right now?{' '}
                  <Link to="/" className="font-medium text-primary-600 underline">
                    Check in from Today
                  </Link>{' '}
                  — this page records what already happened.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => setShowManualCallEntry(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Add time
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Week Tab */}
        {!isLoading && activeTab === 'week' && weekData && (
          <>
            {/* Week Summary */}
            <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                This Week
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-2xl font-bold text-slate-800">
                    {weekData.totalHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-slate-500">Total Hours</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-2xl font-bold text-slate-800">
                    {weekData.totalMileage.toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">Total Miles</div>
                </div>
                <div className="rounded-lg bg-green-50 p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {weekData.storeHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-green-600">Store Time</div>
                </div>
                <div className="rounded-lg bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {weekData.driveHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-blue-600">Drive Time</div>
                </div>
              </div>
            </div>

            {/* Daily Breakdown - Expandable */}
            <div className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Daily Breakdown
                <span className="ml-2 text-xs font-normal normal-case text-slate-400">
                  (tap day to view entries)
                </span>
              </h2>
              {weekData.days.every(d => d.hours === 0) ? (
                <div className="py-8 text-center text-slate-500">
                  <p>No time entries this week</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {weekData.days.map((day) => {
                    const isExpanded = expandedDay === `week-${day.day}`;
                    const dayDateObj = day.date ? new Date(day.date + 'T12:00:00') : null;
                    const isDayEditable = dayDateObj ? isDateEditable(dayDateObj) : false;

                    return (
                      <div key={day.day} className="rounded-lg border border-slate-200 overflow-hidden">
                        {/* Day header - clickable */}
                        <button
                          onClick={() => setExpandedDay(isExpanded ? null : `week-${day.day}`)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-slate-50"
                        >
                          <div className="w-20 text-sm font-medium text-slate-600">
                            {day.day}
                            {dayDateObj && (
                              <span className="ml-1 text-xs text-slate-400">
                                {dayDateObj.getMonth() + 1}/{dayDateObj.getDate()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div
                              className="h-5 rounded-full bg-primary-500"
                              style={{ width: `${Math.max((day.hours / 10) * 100, day.hours > 0 ? 5 : 0)}%` }}
                            />
                          </div>
                          <div className="w-20 text-right">
                            <span className="font-medium text-slate-800">{day.hours.toFixed(1)}h</span>
                            <span className="ml-1 text-xs text-slate-500">({day.stores})</span>
                          </div>
                          <svg
                            className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Expanded entries */}
                        {isExpanded && day.entries.length > 0 && (
                          <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2">
                            {!isDayEditable && (
                              <div className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-2">
                                This day is outside the editable period
                              </div>
                            )}
                            {day.entries.map((entry) => {
                              const callLog = entry.type === 'store' && entry.callLogId
                                ? day.callLogs.find(log => {
                                    if (log.calllogid !== entry.callLogId) return false;
                                    // Skip Drive entries - we want the Work entry
                                    const activity = log.description_of_activity || '';
                                    return !activity.includes('Drive');
                                  })
                                : null;
                              const isCompleted = entry.duration !== 'Active';
                              const canEdit = entry.type === 'store' && isCompleted && callLog && isDayEditable;
                              const isSigned = callLog ? isVisitSigned(callLog) : false;
                              const isSkipped = callLog ? isVisitSkipped(callLog) : false;

                              return (
                                <div
                                  key={entry.id}
                                  className={`flex items-center gap-2 rounded-lg border p-2 ${
                                    entry.type === 'store'
                                      ? 'border-green-200 bg-green-50'
                                      : 'border-blue-200 bg-blue-50'
                                  }`}
                                >
                                  <div
                                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                      entry.type === 'store' ? 'bg-green-500' : 'bg-blue-500'
                                    } text-white`}
                                  >
                                    {entry.type === 'store' ? (
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                      </svg>
                                    ) : (
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium text-slate-800 text-sm truncate">{entry.store}</span>
                                      {isSigned && (
                                        <span className="flex items-center gap-0.5 rounded-full bg-green-100 px-1 py-0.5 text-[10px] font-medium text-green-700">
                                          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </span>
                                      )}
                                      {isSkipped && (
                                        <span
                                          className="flex items-center gap-0.5 rounded-full bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-700"
                                          title={callLog?.no_signature_reason || undefined}
                                        >
                                          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {entry.start} - {entry.end}
                                    </div>
                                    {entry.type === 'drive' && entry.miles != null && (
                                      <div className="text-[10px] font-medium text-blue-600">{entry.miles} mi</div>
                                    )}
                                    {entry.projectId && entry.type === 'store' && (
                                      <div className="text-[10px] text-primary-600 truncate">
                                        Project #{entry.projectId}{entry.projectDescription && `: ${entry.projectDescription}`}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-xs font-medium text-slate-600 bg-white rounded px-1.5 py-0.5">
                                    {entry.duration}
                                  </div>
                                  {canEdit && callLog && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCallLog(callLog);
                                      }}
                                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-slate-600"
                                      title="Edit visit"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                  )}
                                  {canEdit && callLog && !isSigned && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeletingCallLog(callLog);
                                      }}
                                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-100 hover:text-red-600"
                                      title="Delete visit"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {isExpanded && day.entries.length === 0 && (
                          <div className="border-t border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                            No entries for this day
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Prior Week Tab */}
        {!isLoading && activeTab === 'prior' && priorWeekData && (
          <>
            {/* Prior Week Summary */}
            <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Prior Week
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-2xl font-bold text-slate-800">
                    {priorWeekData.totalHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-slate-500">Total Hours</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-2xl font-bold text-slate-800">
                    {priorWeekData.totalMileage.toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">Total Miles</div>
                </div>
                <div className="rounded-lg bg-green-50 p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {priorWeekData.storeHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-green-600">Store Time</div>
                </div>
                <div className="rounded-lg bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {priorWeekData.driveHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-blue-600">Drive Time</div>
                </div>
              </div>
            </div>

            {/* Prior Week Daily Breakdown - Expandable */}
            <div className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Daily Breakdown
                <span className="ml-2 text-xs font-normal normal-case text-slate-400">
                  (tap day to view entries)
                </span>
              </h2>
              {priorWeekData.days.every(d => d.hours === 0) ? (
                <div className="py-8 text-center text-slate-500">
                  <p>No time entries for prior week</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {priorWeekData.days.map((day) => {
                    const isExpanded = expandedDay === `prior-${day.day}`;
                    const dayDateObj = day.date ? new Date(day.date + 'T12:00:00') : null;
                    const isDayEditable = dayDateObj ? isDateEditable(dayDateObj) : false;

                    return (
                      <div key={day.day} className="rounded-lg border border-slate-200 overflow-hidden">
                        {/* Day header - clickable */}
                        <button
                          onClick={() => setExpandedDay(isExpanded ? null : `prior-${day.day}`)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-slate-50"
                        >
                          <div className="w-20 text-sm font-medium text-slate-600">
                            {day.day}
                            {dayDateObj && (
                              <span className="ml-1 text-xs text-slate-400">
                                {dayDateObj.getMonth() + 1}/{dayDateObj.getDate()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div
                              className="h-5 rounded-full bg-primary-500"
                              style={{ width: `${Math.max((day.hours / 10) * 100, day.hours > 0 ? 5 : 0)}%` }}
                            />
                          </div>
                          <div className="w-20 text-right">
                            <span className="font-medium text-slate-800">{day.hours.toFixed(1)}h</span>
                            <span className="ml-1 text-xs text-slate-500">({day.stores})</span>
                          </div>
                          <svg
                            className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Expanded entries */}
                        {isExpanded && day.entries.length > 0 && (
                          <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2">
                            {!isDayEditable && (
                              <div className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-2">
                                This day is outside the editable period
                              </div>
                            )}
                            {day.entries.map((entry) => {
                              const callLog = entry.type === 'store' && entry.callLogId
                                ? day.callLogs.find(log => {
                                    if (log.calllogid !== entry.callLogId) return false;
                                    // Skip Drive entries - we want the Work entry
                                    const activity = log.description_of_activity || '';
                                    return !activity.includes('Drive');
                                  })
                                : null;
                              const isCompleted = entry.duration !== 'Active';
                              const canEdit = entry.type === 'store' && isCompleted && callLog && isDayEditable;
                              const isSigned = callLog ? isVisitSigned(callLog) : false;
                              const isSkipped = callLog ? isVisitSkipped(callLog) : false;

                              return (
                                <div
                                  key={entry.id}
                                  className={`flex items-center gap-2 rounded-lg border p-2 ${
                                    entry.type === 'store'
                                      ? 'border-green-200 bg-green-50'
                                      : 'border-blue-200 bg-blue-50'
                                  }`}
                                >
                                  <div
                                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                      entry.type === 'store' ? 'bg-green-500' : 'bg-blue-500'
                                    } text-white`}
                                  >
                                    {entry.type === 'store' ? (
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                      </svg>
                                    ) : (
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium text-slate-800 text-sm truncate">{entry.store}</span>
                                      {isSigned && (
                                        <span className="flex items-center gap-0.5 rounded-full bg-green-100 px-1 py-0.5 text-[10px] font-medium text-green-700">
                                          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </span>
                                      )}
                                      {isSkipped && (
                                        <span
                                          className="flex items-center gap-0.5 rounded-full bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-700"
                                          title={callLog?.no_signature_reason || undefined}
                                        >
                                          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {entry.start} - {entry.end}
                                    </div>
                                    {entry.type === 'drive' && entry.miles != null && (
                                      <div className="text-[10px] font-medium text-blue-600">{entry.miles} mi</div>
                                    )}
                                    {entry.projectId && entry.type === 'store' && (
                                      <div className="text-[10px] text-primary-600 truncate">
                                        Project #{entry.projectId}{entry.projectDescription && `: ${entry.projectDescription}`}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-xs font-medium text-slate-600 bg-white rounded px-1.5 py-0.5">
                                    {entry.duration}
                                  </div>
                                  {canEdit && callLog && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCallLog(callLog);
                                      }}
                                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-slate-600"
                                      title="Edit visit"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                  )}
                                  {canEdit && callLog && !isSigned && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeletingCallLog(callLog);
                                      }}
                                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-100 hover:text-red-600"
                                      title="Delete visit"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {isExpanded && day.entries.length === 0 && (
                          <div className="border-t border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                            No entries for this day
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Manual Call Entry Modal - supports both add and edit modes */}
      <ManualCallEntry
        isOpen={showManualCallEntry}
        onClose={handleCloseManualEntry}
        editingCallLog={editingCallLog}
        onSaveComplete={handleSaveComplete}
        initialSpan={gapSpan}
      />

      {/* The one add verb for a hole in the day: what filled this time? */}
      {fillingGap && !showManualCallEntry && (
        <GapFillChooser
          gap={fillingGap}
          driveCandidate={gapDriveOffer?.candidate ?? null}
          onStoreVisit={() => setShowManualCallEntry(true)}
          onDrive={() => {
            if (!gapDriveOffer) return;
            setEditingCallLog(gapDriveOffer.row);
            setShowManualCallEntry(true);
          }}
          onClose={() => setFillingGap(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingCallLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Delete Time Entry?</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>

            <div className="mb-4 rounded-lg bg-slate-50 p-3">
              <div className="text-sm font-medium text-slate-800">
                {deletingCallLog.retailer_abbreviation
                  ? `${deletingCallLog.retailer_abbreviation} #${deletingCallLog.store_legacystoreid}`
                  : `Store ${deletingCallLog.store_legacystoreid}`}
              </div>
              <div className="text-xs text-slate-500">
                {formatTime(deletingCallLog.stcalltime)} - {formatTime(deletingCallLog.edcalltime)}
              </div>
              {deletingCallLog.project_description && (
                <div className="mt-1 text-xs text-primary-600">
                  {deletingCallLog.project_description}
                </div>
              )}
            </div>

            <p className="mb-4 text-sm text-slate-600">
              This will permanently delete this time entry and any associated photos, documents, and survey responses.
            </p>

            {deleteError && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {deleteError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDeletingCallLog(null);
                  setDeleteError(null);
                }}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 font-medium text-slate-700 hover:bg-slate-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCallLog}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 font-medium text-white hover:bg-red-700 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  'Delete Entry'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
